import { faArrowsUpDown } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Loader } from '@rocketlane/ui';
import { useEmailMessagesQuery } from 'queries/email-messages';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { RenderEmailAttachment } from './email-attachment';
import { EmailMessage } from './email-message';

const Scroll = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  overflow-y: auto;
  padding: var(--spacing-05) var(--spacing-09);
`;

const Content = styled.div`
  width: 100%;
  max-width: 1500px;
  margin: 0 auto;
`;

const Centered = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  color: var(--text-tertiary);
`;

const CollapseBadgeRow = styled.div`
  display: flex;
  align-items: center;
  padding: var(--spacing-03) 0;
  cursor: pointer;
`;

const CollapseBadgeLine = styled.div`
  flex: 1;
  height: 2px;
  background: var(--cool-gray-10, #f4f4f4);
  border-radius: 1px;
  transition: background 150ms ease;

  ${CollapseBadgeRow}:hover & {
    background: var(--blue-50, #3e63dd);
  }
`;

const CollapseBadgeLabel = styled.span`
  opacity: 1;
  transition: opacity 150ms ease;
  ${CollapseBadgeRow}:hover & {
    opacity: 0;
  }
`;

const CollapseBadgeIcon = styled.span`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  opacity: 0;
  transition: opacity 150ms ease;
  ${CollapseBadgeRow}:hover & {
    opacity: 1;
  }
`;

const CollapseBadgeButton = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border-radius: 50%;
  background: transparent;
  border: 1px solid var(--cool-gray-30, #c6c6c6);
  font-size: 11px;
  font-weight: 600;
  color: var(--text-secondary);
  cursor: pointer;
  flex-shrink: 0;
  transition:
    border-color 150ms ease,
    color 150ms ease;

  ${CollapseBadgeRow}:hover & {
    border-color: var(--blue-50, #3e63dd);
    color: var(--blue-50, #3e63dd);
  }
`;

/**
 * Number of email messages whose iframes are mounted at any given time.
 */
const WINDOW_SIZE = 15;
const OVERSCAN_ABOVE = 1;
const ESTIMATED_MESSAGE_HEIGHT = 360;

/** Threads with more than this many messages get the middle collapsed. */
const COLLAPSE_THRESHOLD = 3;

export interface EmailTranscriptHandle {
  expandAll: () => void;
  collapseAll: () => void;
}

interface Props {
  sourceId: number;
  renderAttachment?: RenderEmailAttachment;
  emailMessageId?: number;
  onIframeClick?: () => void;
}

export const EmailTranscript = forwardRef<EmailTranscriptHandle, Props>(
  ({ sourceId, renderAttachment, emailMessageId, onIframeClick }, ref) => {
    const { data, isLoading, error } = useEmailMessagesQuery(sourceId);

    const messages = useMemo(() => data ?? [], [data]);
    const total = messages.length;

    // Set of message ids the user has explicitly expanded beyond the default.
    const [expandedIds, setExpandedIds] = useState<ReadonlySet<number>>(
      new Set(),
    );
    // Set of message ids the user has explicitly collapsed (overrides auto-expand of lastId).
    const [collapsedIds, setCollapsedIds] = useState<ReadonlySet<number>>(
      new Set(),
    );
    // Whether the middle messages are collapsed (only relevant when total > COLLAPSE_THRESHOLD).
    const [middleCollapsed, setMiddleCollapsed] = useState(true);

    // Reset on thread change.
    useEffect(() => {
      setExpandedIds(new Set());
      setCollapsedIds(new Set());
      setMiddleCollapsed(true);
    }, [sourceId]);

    const lastId = messages[total - 1]?.emailMessageId;

    // Tracks whether we have already scrolled to emailMessageId for the current
    // sourceId so we don't re-scroll on every heights update.
    const scrolledToRef = useRef<number | null>(null);

    useEffect(() => {
      scrolledToRef.current = null;
    }, [sourceId]);

    const isExpanded = useCallback(
      (id: number) =>
        (id === lastId || expandedIds.has(id)) && !collapsedIds.has(id),
      [lastId, expandedIds, collapsedIds],
    );

    const expandMessage = useCallback((id: number) => {
      setCollapsedIds((prev) => {
        const next = new Set(prev);

        next.delete(id);

        return next;
      });
      setExpandedIds((prev) => new Set([...prev, id]));
    }, []);

    const collapseMessage = useCallback((id: number) => {
      setCollapsedIds((prev) => new Set([...prev, id]));
      setExpandedIds((prev) => {
        const next = new Set(prev);

        next.delete(id);

        return next;
      });
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        expandAll: () => {
          setExpandedIds(new Set(messages.map((m) => m.emailMessageId)));
          setCollapsedIds(new Set());
          setMiddleCollapsed(false);
        },
        collapseAll: () => {
          setExpandedIds(new Set());
          setCollapsedIds(
            new Set(messages.slice(0, -1).map((m) => m.emailMessageId)),
          );
          setMiddleCollapsed(false);
        },
      }),
      [messages],
    );

    // --- Windowing (used when showing all messages) ---
    const scrollRef = useRef<HTMLDivElement>(null);
    const wrapperRefs = useRef(new Map<number, HTMLDivElement>());
    const messageIdsRef = useRef<number[]>([]);

    messageIdsRef.current = messages.map((m) => m.emailMessageId);

    const [heights, setHeights] = useState<Record<number, number>>({});
    const [anchorIndex, setAnchorIndex] = useState(0);

    const maxStart = Math.max(0, total - WINDOW_SIZE);
    const start = Math.min(maxStart, Math.max(0, anchorIndex - OVERSCAN_ABOVE));
    const end = Math.min(total, start + WINDOW_SIZE);

    useEffect(() => {
      setHeights({});
      setAnchorIndex(0);
      scrollRef.current?.scrollTo({ top: 0 });
    }, [sourceId]);

    // When emailMessageId is provided, expand the middle (if needed), force-expand
    // the target message, and shift the virtual window to include it.
    useEffect(() => {
      if (!emailMessageId || isLoading || total === 0) {
        return;
      }
      if (scrolledToRef.current === emailMessageId) {
        return;
      }

      const targetIndex = messages.findIndex(
        (m) => m.emailMessageId === emailMessageId,
      );

      if (targetIndex === -1) {
        return;
      }

      const isFirst = targetIndex === 0;
      const isSecondToLast = targetIndex === total - 2;
      const isLast = targetIndex === total - 1;

      if (
        total > COLLAPSE_THRESHOLD &&
        middleCollapsed &&
        !isFirst &&
        !isSecondToLast &&
        !isLast
      ) {
        setMiddleCollapsed(false);
      }

      setCollapsedIds((prev) => {
        const next = new Set(prev);

        next.delete(emailMessageId);

        return next;
      });
      setExpandedIds((prev) => new Set([...prev, emailMessageId]));
      setAnchorIndex(Math.max(0, targetIndex - OVERSCAN_ABOVE));
    }, [emailMessageId, isLoading, messages, total, middleCollapsed]);

    // After ResizeObserver measures the target element (heights updated), scroll to it.
    useEffect(() => {
      if (!emailMessageId || scrolledToRef.current === emailMessageId) {
        return;
      }
      const el = wrapperRefs.current.get(emailMessageId);

      if (!el) {
        return;
      }
      scrolledToRef.current = emailMessageId;
      requestAnimationFrame(() => {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }, [emailMessageId, heights]);

    const setWrapperRef = useCallback(
      (id: number) => (el: HTMLDivElement | null) => {
        if (el) {
          wrapperRefs.current.set(id, el);
        } else {
          wrapperRefs.current.delete(id);
        }
      },
      [],
    );

    const updateAnchor = useCallback(() => {
      const scrollEl = scrollRef.current;

      if (!scrollEl) {
        return;
      }
      const ids = messageIdsRef.current;
      const containerTop = scrollEl.getBoundingClientRect().top;
      let next = Math.max(0, ids.length - 1);

      for (let i = 0; i < ids.length; i += 1) {
        const el = wrapperRefs.current.get(ids[i]);

        if (!el) {
          continue;
        }
        if (el.getBoundingClientRect().bottom - containerTop > 0) {
          next = i;
          break;
        }
      }
      setAnchorIndex((prev) => (prev === next ? prev : next));
    }, []);

    const tickingRef = useRef(false);
    const handleScroll = useCallback(() => {
      if (tickingRef.current) {
        return;
      }
      tickingRef.current = true;
      requestAnimationFrame(() => {
        tickingRef.current = false;
        updateAnchor();
      });
    }, [updateAnchor]);

    useEffect(() => {
      const observer = new ResizeObserver((entries) => {
        setHeights((prev) => {
          let changed = false;
          const next = { ...prev };

          for (const entry of entries) {
            const id = Number(
              entry.target.getAttribute('data-message-id') ?? -1,
            );
            const h = (entry.target as HTMLElement).offsetHeight;

            if (id >= 0 && h > 0 && next[id] !== h) {
              next[id] = h;
              changed = true;
            }
          }

          return changed ? next : prev;
        });
      });

      for (let i = start; i < end; i += 1) {
        const el = wrapperRefs.current.get(messages[i].emailMessageId);

        if (el) {
          observer.observe(el);
        }
      }

      return () => observer.disconnect();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [start, end, data]);

    useLayoutEffect(() => {
      updateAnchor();
    }, [heights, total, updateAnchor]);

    // Collapsed middle messages count badge label
    // We show first + last 2, so the badge covers total - 3 messages.
    const collapsedCount = useMemo(
      () => (total > COLLAPSE_THRESHOLD ? total - 3 : 0),
      [total],
    );
    const showCollapsedMiddle = total > COLLAPSE_THRESHOLD && middleCollapsed;

    if (isLoading) {
      return (
        <Centered>
          <Loader />
        </Centered>
      );
    }
    if (error) {
      return (
        <Centered>
          <FormattedMessage
            id="account.email_pages.transcript.error"
            defaultMessage="Failed to load messages"
          />
        </Centered>
      );
    }
    if (total === 0) {
      return (
        <Centered>
          <FormattedMessage
            id="account.email_pages.transcript.empty"
            defaultMessage="No messages available"
          />
        </Centered>
      );
    }

    // --- Collapsed middle view: first + badge + last 2 ---
    if (showCollapsedMiddle) {
      const first = messages[0];
      const secondToLast = messages[total - 2];
      const last = messages[total - 1];

      return (
        <Scroll ref={scrollRef}>
          <Content>
            <div
              data-message-id={first.emailMessageId}
              ref={setWrapperRef(first.emailMessageId)}
            >
              <EmailMessage
                message={first}
                renderAttachment={renderAttachment}
                collapsed={!isExpanded(first.emailMessageId)}
                onExpand={() => expandMessage(first.emailMessageId)}
                onCollapse={() => collapseMessage(first.emailMessageId)}
                onIframeClick={onIframeClick}
                hideBorderBottom={collapsedCount > 0}
              />
            </div>

            {collapsedCount > 0 && (
              <CollapseBadgeRow
                onClick={() => {
                  setMiddleCollapsed(false);
                  scrollRef.current?.scrollTo({ top: 0 });
                }}
              >
                <CollapseBadgeButton type="button" title="Show all messages">
                  <CollapseBadgeLabel>{collapsedCount}</CollapseBadgeLabel>
                  <CollapseBadgeIcon>
                    <FontAwesomeIcon icon={faArrowsUpDown} />
                  </CollapseBadgeIcon>
                </CollapseBadgeButton>
                <CollapseBadgeLine />
              </CollapseBadgeRow>
            )}

            <div
              data-message-id={secondToLast.emailMessageId}
              ref={setWrapperRef(secondToLast.emailMessageId)}
            >
              <EmailMessage
                message={secondToLast}
                renderAttachment={renderAttachment}
                collapsed={!isExpanded(secondToLast.emailMessageId)}
                onExpand={() => expandMessage(secondToLast.emailMessageId)}
                onCollapse={() => collapseMessage(secondToLast.emailMessageId)}
                onIframeClick={onIframeClick}
              />
            </div>

            <div
              data-message-id={last.emailMessageId}
              ref={setWrapperRef(last.emailMessageId)}
            >
              <EmailMessage
                message={last}
                renderAttachment={renderAttachment}
                collapsed={!isExpanded(last.emailMessageId)}
                onExpand={() => expandMessage(last.emailMessageId)}
                onCollapse={() => collapseMessage(last.emailMessageId)}
                onIframeClick={onIframeClick}
                hideBorderBottom
              />
            </div>
          </Content>
        </Scroll>
      );
    }

    // --- All-messages view (with windowing) ---
    return (
      <Scroll ref={scrollRef} onScroll={handleScroll}>
        <Content>
          {messages.map((message, index) => {
            const id = message.emailMessageId;
            const isMounted = index >= start && index < end;
            const isLast = index === total - 1;

            if (isMounted) {
              return (
                <div key={id} data-message-id={id} ref={setWrapperRef(id)}>
                  <EmailMessage
                    message={message}
                    renderAttachment={renderAttachment}
                    collapsed={!isExpanded(id)}
                    onExpand={() => expandMessage(id)}
                    onCollapse={() => collapseMessage(id)}
                    onIframeClick={onIframeClick}
                    hideBorderBottom={isLast}
                  />
                </div>
              );
            }

            return (
              <div
                key={id}
                data-message-id={id}
                ref={setWrapperRef(id)}
                style={{ height: heights[id] ?? ESTIMATED_MESSAGE_HEIGHT }}
                aria-hidden
              />
            );
          })}
        </Content>
      </Scroll>
    );
  },
);

EmailTranscript.displayName = 'EmailTranscript';
